/**
 * 形态档位判定 + 尺寸突变处理（docs/DEVICES.md R1 / R2）。
 *
 * 为什么需要一个专门的模块，而不是在组件里读一次 `innerWidth`：
 *
 *  - **尺寸会在牌局进行中突变。** 折叠机展开/折叠 = 手机档 ⇄ 平板档，
 *    微信工具栏收起、转屏、Android 分屏拖动同理。挂载时算一次就固定，
 *    展开的那一瞬间座位会飞出屏幕，而且再也回不来。
 *  - **换档不能打断手牌。** 所以这里**只做两件事**：更新一个 store 的快照、
 *    在 `<html>` 上换一个 `data-viewport` 属性。
 *    绝不卸载/重建任何组件 —— 一旦按档位换 key，RaiseControl 会被重建，
 *    玩家手指还按在滑块上，没提交的加注金额就没了（R1 最后一条）。
 *  - **边界要有迟滞。** 微信工具栏收放会让高度在阈值附近反复横跳，
 *    没有迟滞就会一秒钟换十次档，每次都重排整张牌桌。
 *
 * 判档规则本身在 `table/seatGeometry.ts`（`viewportClass`）—— 它是纯几何，
 * 不能因为「要给 React 用」就被拖进一个带 useEffect 的文件里。
 */

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'

import '../styles/viewport.css'
import { isKeyboardLocked } from '../styles/keyboardLock.js'
import { applyViewportClass, installViewportUnits } from '../styles/viewportUnits.js'
import { TABLET_MIN_HEIGHT, TABLET_MIN_SHORT_SIDE, viewportClass } from '../table/seatGeometry.js'
import type { StageSize, ViewportClass } from '../table/seatGeometry.js'

export type { ViewportClass } from '../table/seatGeometry.js'

export type Orientation = 'portrait' | 'landscape' | 'square'

export interface ViewportState {
  readonly width: number
  readonly height: number
  readonly mode: ViewportClass
  readonly orientation: Orientation
  /** width / height。接近 1 就是折叠机展开态那种方屏 */
  readonly aspect: number
  /**
   * 视口小到**真的放不下**一张牌桌。判据是最小可用宽/高，与朝向、宽高比无关：
   * 横屏、方屏都不会仅仅因为「不是竖的」就被判定为不可用（DEVICES.md R2）。
   */
  readonly tooSmall: boolean
  /** 具体是哪一维不够（给用户看的原话）。tooSmall 为 false 时是 null。 */
  readonly tooSmallReason: string | null
  /** @deprecated `tooSmall` 的旧名。**绝不能**再用它来拒绝渲染或弹「请竖屏使用」。 */
  readonly rotateHint: boolean
  /** 每次真实尺寸变化 +1。动画层可以用它判断「这一帧是重排，不是动效」 */
  readonly revision: number
}

/** 换档迟滞：越过阈值 24px 才真的换，避免工具栏收放时在边界上反复横跳 */
export const MODE_HYSTERESIS_PX = 24

/**
 * 「放得下」的下限。**按可用宽高判断，不按朝向、不按宽高比。**
 *
 * 数值来源（都是量出来的，不是拍的）：
 *  - 宽 300px：三张公共牌 + 两侧最窄座位块（tight 档 58px×2）+ 间隙的和。
 *  - 高 460px：顶栏 48 + dock 最矮一档 100 + 底部安全区 10 = 158 的壳层开销，
 *    剩 302px 给舞台 —— 这是座位几何在 2..8 人下仍能不重叠的最矮舞台。
 *
 * 低于任一维才降级提示，并且要说清是哪一维不够（见 `tooSmallReason`）。
 * 旧版按 `height < 500` 一刀切，把 691×634 的折叠机展开态（D1 实测）也挡了 —— 那是
 * 它的**正常形态**，不是拿反了。
 */
export const MIN_USABLE_WIDTH = 300
export const MIN_USABLE_HEIGHT = 460
/** @deprecated `MIN_USABLE_HEIGHT` 的旧名，只为老引用不报错 */
export const ROTATE_HINT_MAX_H = MIN_USABLE_HEIGHT
/** 换档后禁 CSS 动效的时长，与 viewport.css 的 .viewport-shifting 配套 */
export const SHIFT_SETTLE_MS = 200

/** SSR / 静态渲染时的默认值：直接用主基准机型，免得首帧按 0×0 算出一桌重叠的座位 */
export const DEFAULT_VIEWPORT: StageSize = { width: 440, height: 856 }

function orientationOf(size: StageSize): Orientation {
  if (size.width > size.height * 1.05) return 'landscape'
  if (size.height > size.width * 1.05) return 'portrait'
  return 'square'
}

/**
 * 带迟滞的换档。`prev` 为空（首次测量）时直接按阈值判。
 *
 * 迟滞的方向要分别处理：
 *  - 手机 → 平板：要求**比阈值再宽/高 24px**，免得刚够 600 的短边抖一下就换档。
 *  - 平板 → 手机：要求**比阈值再窄/矮 24px**。
 * 两边都留 24px，于是边界上有一条 48px 宽的死区，工具栏收放（通常 40~50px）
 * 不再会引发换档 —— 除非它真的把设备推过了另一档。
 */
export function nextViewportMode(size: StageSize, prev?: ViewportClass): ViewportClass {
  const raw = viewportClass(size)
  if (prev === undefined || prev === raw) return raw
  if (raw === 'tablet') {
    const shrunk = { width: size.width - MODE_HYSTERESIS_PX, height: size.height - MODE_HYSTERESIS_PX }
    return viewportClass(shrunk) === 'tablet' ? 'tablet' : prev
  }
  const grown = { width: size.width + MODE_HYSTERESIS_PX, height: size.height + MODE_HYSTERESIS_PX }
  return viewportClass(grown) === 'phone' ? 'phone' : prev
}

/**
 * 视口小到放不下时的具体原因。**两维分开说**，因为处置方式不同：
 * 高度不够通常是软键盘/工具栏占了地方（收起来就好），宽度不够是设备本身太窄（没救）。
 * 返回 null 表示放得下。
 */
export function tooSmallReasonOf(size: StageSize): string | null {
  const narrow = size.width < MIN_USABLE_WIDTH
  const short = size.height < MIN_USABLE_HEIGHT
  if (narrow && short) return `窗口只有 ${size.width}×${size.height}，牌桌需要至少 ${MIN_USABLE_WIDTH}×${MIN_USABLE_HEIGHT}`
  if (narrow) return `宽度只有 ${size.width}px，牌桌需要至少 ${MIN_USABLE_WIDTH}px`
  if (short) return `高度只有 ${size.height}px，牌桌需要至少 ${MIN_USABLE_HEIGHT}px（收起键盘或浏览器工具栏可以腾出空间）`
  return null
}

/** 由尺寸与档位组装一个不可变快照 */
export function buildViewportState(size: StageSize, mode: ViewportClass, revision: number): ViewportState {
  const height = Math.max(0, size.height)
  const width = Math.max(0, size.width)
  // ★ 只看「放不放得下」，不看朝向：横屏与方屏都是一等形态（R2）。
  //   691×634、1000×620 这类平板档尺寸自然落在阈值之上，不会被判定为不可用。
  const reason = tooSmallReasonOf({ width, height })
  return {
    width,
    height,
    mode,
    orientation: orientationOf({ width, height }),
    aspect: height === 0 ? 0 : width / height,
    tooSmall: reason !== null,
    tooSmallReason: reason,
    rotateHint: reason !== null,
    revision,
  }
}

// ============================================================================
// tracker：可在 node 里完整测试的核心
// ============================================================================

export interface ViewportTrackerHost {
  /** 量一次当前视口 */
  measure(): StageSize
  /** 订阅尺寸变化（ResizeObserver / visualViewport / orientationchange），返回退订 */
  subscribe(onResize: () => void): () => void
  /** 节流到下一帧；测试注入同步实现。返回取消函数。 */
  schedule?(fn: () => void): () => void
  /** 换档时的副作用（写 data-viewport、挂 .viewport-shifting）。纯测试时可省。 */
  onModeChange?(mode: ViewportClass, prev: ViewportClass | null): void
}

export interface ViewportTracker {
  /** useSyncExternalStore 的 getSnapshot：同一状态恒返回同一个对象 */
  state(): ViewportState
  /** useSyncExternalStore 的 subscribe：无参通知 */
  subscribe(listener: () => void): () => void
  /** 立刻重量一次（bfcache 回来、手动 retry 时用） */
  measureNow(): void
}

export function createViewportTracker(host: ViewportTrackerHost): ViewportTracker {
  const schedule = host.schedule ?? ((fn: () => void) => {
    if (typeof requestAnimationFrame !== 'function') {
      fn()
      return () => {}
    }
    const id = requestAnimationFrame(fn)
    return () => cancelAnimationFrame(id)
  })

  const first = host.measure()
  let mode = nextViewportMode(first)
  let current = buildViewportState(first, mode, 0)
  const listeners = new Set<() => void>()
  let detach: (() => void) | null = null
  let cancelFrame: (() => void) | null = null
  let pending = false

  const apply = (): void => {
    pending = false
    cancelFrame = null
    const size = host.measure()
    // 尺寸没变就什么都不做：ResizeObserver 在 iOS 上一次转屏能连发十几次，
    // 每次都发通知会让整棵树白重渲染十几遍。
    if (size.width === current.width && size.height === current.height) return
    const prev = mode
    mode = nextViewportMode(size, mode)
    current = buildViewportState(size, mode, current.revision + 1)
    // 副作用只在**真的换档**时触发：每像素都挂一次「禁动效」会把正常动画也掐掉
    if (mode !== prev) host.onModeChange?.(mode, prev)
    for (const fn of listeners) fn()
  }

  const onResize = (): void => {
    if (pending) return
    pending = true
    cancelFrame = schedule(apply)
  }

  const start = (): void => {
    if (detach !== null) return
    detach = host.subscribe(onResize)
    // 挂载与首次测量之间可能已经变过（首屏字体/图片回流），补量一次
    apply()
  }

  const stop = (): void => {
    detach?.()
    detach = null
    if (cancelFrame !== null) cancelFrame()
    cancelFrame = null
    pending = false
  }

  // 首帧就把档位写出去，免得 CSS 先按默认档画一帧再跳
  host.onModeChange?.(mode, null)

  return {
    state: () => current,
    subscribe(listener) {
      listeners.add(listener)
      if (listeners.size === 1) start()
      return () => {
        listeners.delete(listener)
        if (listeners.size === 0) stop()
      }
    },
    measureNow: apply,
  }
}

// ============================================================================
// 浏览器接线
// ============================================================================

/**
 * 真实环境的 host。
 *
 * 量哪个尺寸：优先 `visualViewport`（它排除了微信导航条，正是 DEVICES.md 里
 * 实测到的 440×856 与 691×634），捏合放大时退回 `innerWidth/innerHeight`
 * —— 放大中的 visualViewport 是「取景框」，跟着它换档会让页面在放大瞬间塌掉。
 */
export function browserViewportHost(): ViewportTrackerHost {
  // 打字期间（软键盘弹起）沿用上一次量值：折叠屏上键盘会把 vv 压到 691×330，
  // 照量就会被判成手机档 —— 换美术、掐动画，而那只是个键盘
  let last: StageSize | null = null
  return {
    measure(): StageSize {
      if (isKeyboardLocked() && last !== null) return last
      const vv = globalThis.visualViewport
      const zoomed = (vv?.scale ?? 1) > 1.01
      if (vv != null && !zoomed && vv.width > 0 && vv.height > 0) {
        last = { width: Math.round(vv.width), height: Math.round(vv.height) }
        return last
      }
      const el = globalThis.document?.documentElement
      last = {
        width: Math.round(el?.clientWidth || globalThis.innerWidth || DEFAULT_VIEWPORT.width),
        height: Math.round(el?.clientHeight || globalThis.innerHeight || DEFAULT_VIEWPORT.height),
      }
      return last
    },

    subscribe(onResize): () => void {
      const vv = globalThis.visualViewport
      const el = globalThis.document?.documentElement ?? null
      globalThis.addEventListener('resize', onResize)
      globalThis.addEventListener('orientationchange', onResize)
      globalThis.addEventListener('pageshow', onResize)
      vv?.addEventListener('resize', onResize)
      // ★ R1 的核心：折叠机展开只改元素尺寸，某些机型上**不发 window.resize**，
      //   只有 ResizeObserver 能看见。
      let ro: ResizeObserver | null = null
      if (typeof ResizeObserver !== 'undefined' && el !== null) {
        ro = new ResizeObserver(onResize)
        ro.observe(el)
      }
      return () => {
        globalThis.removeEventListener('resize', onResize)
        globalThis.removeEventListener('orientationchange', onResize)
        globalThis.removeEventListener('pageshow', onResize)
        vv?.removeEventListener('resize', onResize)
        ro?.disconnect()
      }
    },

    onModeChange(mode): void {
      applyViewportClass(mode)
      markViewportShifting()
    },
  }
}

let shiftTimer: ReturnType<typeof setTimeout> | null = null

/**
 * 换档的那一帧禁掉 CSS 动效（viewport.css 的 `.viewport-shifting`）。
 *
 * 只禁 CSS transition/animation，**不碰 WAAPI** —— 发牌、推池、FLIP 都是
 * `element.animate()`，它们不受这条影响，所以正在飞的筹码不会僵在半空。
 */
export function markViewportShifting(ms: number = SHIFT_SETTLE_MS): void {
  const root = globalThis.document?.documentElement
  if (!root) return
  root.classList.add('viewport-shifting')
  if (shiftTimer !== null) clearTimeout(shiftTimer)
  shiftTimer = setTimeout(() => {
    shiftTimer = null
    root.classList.remove('viewport-shifting')
  }, ms)
}

// ============================================================================
// React 出口
// ============================================================================

let shared: ViewportTracker | null = null

/** 全应用共用一个 tracker：八个座位各自挂一个 ResizeObserver 是纯浪费 */
export function sharedViewportTracker(): ViewportTracker {
  shared ??= createViewportTracker(browserViewportHost())
  return shared
}

/** 仅供测试：换掉/清空共享实例 */
export function __setSharedViewportTracker(t: ViewportTracker | null): void {
  shared = t
}

const SERVER_STATE: ViewportState = buildViewportState(DEFAULT_VIEWPORT, 'phone', 0)

/**
 * 订阅完整视口状态。尺寸每变一次就返回一个新快照（宽高是真数据，消费者需要）。
 *
 * 只关心档位的组件请用 `useViewportModeOnly()` —— 它在尺寸变化但没换档时
 * 返回同一个字符串，不会触发重渲染。
 */
export function useViewportMode(tracker: ViewportTracker = sharedViewportTracker()): ViewportState {
  return useSyncExternalStore(tracker.subscribe, tracker.state, () => SERVER_STATE)
}

/** 只要档位。尺寸在档位内怎么变都不会引起重渲染。 */
export function useViewportModeOnly(tracker: ViewportTracker = sharedViewportTracker()): ViewportClass {
  return useSyncExternalStore(
    tracker.subscribe,
    () => tracker.state().mode,
    () => SERVER_STATE.mode,
  )
}

/**
 * 换档回调。用来做「重算但不打断」的收尾工作，例如让动画层丢掉缓存的旧几何。
 * 回调在换档后同步触发，**不要**在里面 setState 出一棵新组件树（见文件头 R1）。
 */
export function useOnViewportModeChange(
  fn: (mode: ViewportClass, prev: ViewportClass) => void,
  tracker: ViewportTracker = sharedViewportTracker(),
): void {
  const ref = useRef(fn)
  ref.current = fn
  const modeRef = useRef<ViewportClass | null>(null)
  const listener = useCallback(() => {
    const mode = tracker.state().mode
    const prev = modeRef.current
    modeRef.current = mode
    if (prev !== null && prev !== mode) ref.current(mode, prev)
  }, [tracker])

  useEffect(() => {
    modeRef.current = tracker.state().mode
    return tracker.subscribe(listener)
  }, [tracker, listener])
}

/**
 * 启动期接线：一行装好「视口单位兜底 + 档位跟踪」。
 *
 * 为什么要在 React 之外也订阅一个空监听：tracker 只有在**有订阅者**时才观察尺寸，
 * 而 `data-viewport` 是给 CSS 用的 —— 哪怕整棵 React 树里没人调 useViewportMode，
 * 折叠机展开时 CSS 也必须跟着换。返回值是卸载函数（HMR 用）。
 *
 * 调用点：main.tsx，与 installGestureGuards / installPeekGuards 并列。
 */
export function installViewport(): () => void {
  const stopUnits = installViewportUnits()
  const stopTracker = sharedViewportTracker().subscribe(() => {})
  return () => {
    stopUnits()
    stopTracker()
  }
}

/** 便捷判据，给「要不要用平板版面」这种一行的地方用。注意它**不是**「要不要提示旋转」——
 *  横屏/方屏一律正常渲染，能不能放下由 `tooSmall` 单独判定。 */
export function isTabletViewport(size: StageSize): boolean {
  return viewportClass(size) === 'tablet'
}

export { TABLET_MIN_HEIGHT, TABLET_MIN_SHORT_SIDE, viewportClass }
